import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { DateTimeDisplay } from '@parkdna/ui/DateTimeDisplay';
import { useTheme } from '@parkdna/ui/theme';
import { forwardRef } from 'react';

import type { SupportRequestDetail } from '../types';
import { deriveDisplayStatus } from '../utils/deriveDisplayStatus';
import { SupportStatusChip } from './SupportStatusChip';

const OPENED_FORMAT: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
};

export interface ConversationHeaderProps {
  request: SupportRequestDetail;
  showBack?: boolean;
  onBack?: () => void;
}

export const ConversationHeader = forwardRef<
  HTMLHeadingElement,
  ConversationHeaderProps
>(function ConversationHeader({ request, showBack = false, onBack }, ref) {
  const [theme] = useTheme();

  const metaParts = [request.id];
  if (request.location) metaParts.push(request.location);

  return (
    <Stack
      direction="row"
      spacing={1.5}
      sx={{
        alignItems: 'center',
        p: 2,
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      {showBack && (
        <IconButton
          aria-label="Back to List"
          onClick={onBack}
          sx={{ minWidth: 44, minHeight: 44 }}
        >
          <ArrowBackIcon />
        </IconButton>
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          ref={ref}
          tabIndex={-1}
          component="h2"
          variant="h5"
          sx={{
            fontWeight: 700,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            outline: 'none',
          }}
        >
          {request.subject}
        </Typography>
        <Stack
          direction="row"
          spacing={0.5}
          sx={{ alignItems: 'center', color: theme.palette.text.secondary }}
        >
          <Typography variant="caption">
            {metaParts.join(' · ')} · Opened
          </Typography>
          <DateTimeDisplay
            dateTime={request.submitted_at}
            locationTimezone={request.location_timezone ?? undefined}
            format={OPENED_FORMAT}
            variant="caption"
            sx={{ color: theme.palette.text.secondary }}
          />
        </Stack>
        {request.follow_up_to && (
          <Typography
            variant="caption"
            sx={{ display: 'block', color: theme.palette.text.secondary }}
          >
            {`Follow-up to ${request.follow_up_to}`}
          </Typography>
        )}
      </Box>
      <SupportStatusChip
        status={deriveDisplayStatus(request.status, request.messages)}
        size="medium"
      />
    </Stack>
  );
});

export default ConversationHeader;
